import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { mergeHolidaysWithPolicy } from './companyHolidayMerge';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import {
  LEAVE_BUCKET_KEYS,
  emptyLeaveBuckets,
  parseDefaultAccrualPerPayRun,
  parsePayrollSchedule,
  type PayrollSchedule,
  type LeaveBucketKey,
} from './leaveAccrual';

export { parsePayrollSchedule, parseDefaultAccrualPerPayRun } from './leaveAccrual';
export type { PayrollSchedule, LeaveBucketKey } from './leaveAccrual';

export { parseCalendarYmd, mergeHolidaysWithPolicy } from './companyHolidayMerge';

export const EMPLOYEE_BENEFITS_SETTINGS_PATH = 'settings/employee_benefits';
export const TIME_OFF_COLLECTION = 'timeOffRequests';
export const PAYROLL_SNAPSHOTS_COLLECTION = 'employeePayrollSnapshots';

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export interface CompanyHoliday {
  id: string;
  name: string;
  /** Local calendar date YYYY-MM-DD */
  date: string;
}

export interface EmployeeBenefitsSettingsDoc {
  holidays: CompanyHoliday[];
  /** Company-level SEP IRA contribution percentage (e.g. 25 = 25%) */
  sepIraContributionPercent: number | null;
  /** Free-text summary for health insurance reimbursement */
  healthInsuranceReimbursementSummary: string;
  updatedAt?: Timestamp | null;
  /** Org default: how many months wide the People team calendar opens (admins set in Settings). */
  peopleHubDefaultCalendarMonthsVisible?: 2 | 3 | 6 | null;
  /** When automatic leave accrual runs (pay dates). */
  payrollSchedule?: PayrollSchedule | null;
  /** Default hours added per pay run per bucket — template when configuring new employees. */
  defaultAccrualPerPayRun?: Partial<Record<LeaveBucketKey, number>> | null;
}

export const defaultEmployeeBenefitsSettings = (): EmployeeBenefitsSettingsDoc => ({
  holidays: mergeHolidaysWithPolicy([]),
  sepIraContributionPercent: null,
  healthInsuranceReimbursementSummary: '',
  peopleHubDefaultCalendarMonthsVisible: null,
  payrollSchedule: null,
  defaultAccrualPerPayRun: null,
});

export type TimeOffType = 'vacation' | 'sick' | 'ooo';
export type TimeOffStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface TimeOffRequest {
  id: string;
  userId: string;
  displayName: string;
  startDate: Timestamp;
  endDate: Timestamp;
  type: TimeOffType;
  status: TimeOffStatus;
  showOnTeamCalendar: boolean;
  /** Admin/EA calendar entry — not a self-service leave request; does not affect accrual. */
  calendarOnly?: boolean;
  createdByUserId?: string;
  createdByName?: string;
  notes?: string;
  reviewedByUserId?: string;
  reviewedByName?: string;
  reviewedAt?: Timestamp | null;
  rejectReason?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/** Stable userId for contractors / guests who are not CRM users. */
export function externalCalendarPersonUserId(): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `external:${id}`;
}

export interface LeaveBucketStored {
  available: number;
  accrualPerPayRun: number;
}

export interface EmployeePayrollSnapshot {
  userId: string;
  email?: string | null;
  displayName?: string | null;
  /** Internal leave accrual profile + running balances. */
  balanceSource?: 'internal' | string;
  leaveBuckets?: Partial<Record<LeaveBucketKey, LeaveBucketStored>>;
  /** Last pay date (yyyy-MM-dd) when scheduled accrual was applied. */
  lastAccrualPayDate?: string | null;
  openingConfiguredAt?: Timestamp | null;
  configuredByUserId?: string | null;
  updatedAt?: Timestamp | null;
  /** @deprecated Legacy display fields */
  syncedAt?: Timestamp | null;
  source?: string;
  balances?: {
    vacationHours?: number | null;
    sickHours?: number | null;
    ptoHours?: number | null;
  };
  qbSyncStatus?: string;
  syncNote?: string;
}

function benefitsSettingsRef() {
  return doc(db, 'settings', 'employee_benefits');
}

export function subscribeEmployeeBenefitsSettings(
  onData: (data: EmployeeBenefitsSettingsDoc) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    benefitsSettingsRef(),
    snap => {
      if (!snap.exists()) {
        onData(defaultEmployeeBenefitsSettings());
        return;
      }
      const d = snap.data() as Record<string, unknown>;
      const rawHolidays = Array.isArray(d.holidays) ? (d.holidays as CompanyHoliday[]) : [];
      const rawMonths = d.peopleHubDefaultCalendarMonthsVisible;
      const peopleHubDefaultCalendarMonthsVisible =
        rawMonths === 2 || rawMonths === 3 || rawMonths === 6 ? rawMonths : null;
      const payrollSchedule = parsePayrollSchedule(d.payrollSchedule);
      const defaultAccrualPerPayRun = parseDefaultAccrualPerPayRun(d.defaultAccrualPerPayRun);
      onData({
        holidays: mergeHolidaysWithPolicy(rawHolidays),
        sepIraContributionPercent:
          typeof d.sepIraContributionPercent === 'number' ? d.sepIraContributionPercent : null,
        healthInsuranceReimbursementSummary:
          typeof d.healthInsuranceReimbursementSummary === 'string'
            ? d.healthInsuranceReimbursementSummary
            : '',
        updatedAt: d.updatedAt instanceof Timestamp ? d.updatedAt : null,
        peopleHubDefaultCalendarMonthsVisible,
        payrollSchedule,
        defaultAccrualPerPayRun: defaultAccrualPerPayRun ?? null,
      });
    },
    err => {
      firestoreSnapshotListenError('peopleHubFirestore', EMPLOYEE_BENEFITS_SETTINGS_PATH, err);
      onError?.(err as Error);
    },
  );
}

export async function saveEmployeeBenefitsSettings(
  data: EmployeeBenefitsSettingsDoc,
): Promise<void> {
  await setDoc(
    benefitsSettingsRef(),
    {
      ...data,
      updatedAt: Timestamp.now(),
    },
    { merge: true },
  );
}

/** Merge payroll schedule + default accrual template (client write; rules: admin or EA). */
export async function savePayrollScheduleFields(input: {
  payrollSchedule: PayrollSchedule;
  defaultAccrualPerPayRun: Partial<Record<LeaveBucketKey, number>> | null;
}): Promise<void> {
  await setDoc(
    benefitsSettingsRef(),
    {
      payrollSchedule: input.payrollSchedule,
      defaultAccrualPerPayRun: input.defaultAccrualPerPayRun ?? null,
      updatedAt: Timestamp.now(),
    },
    { merge: true },
  );
}

export function subscribeTeamVisibleTimeOff(
  onData: (rows: TimeOffRequest[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, TIME_OFF_COLLECTION),
    where('status', '==', 'approved'),
    where('showOnTeamCalendar', '==', true),
  );
  return onSnapshot(
    q,
    snap => {
      const rows: TimeOffRequest[] = [];
      snap.forEach(s => {
        rows.push({ id: s.id, ...(s.data() as Omit<TimeOffRequest, 'id'>) });
      });
      onData(rows);
    },
    err => {
      firestoreSnapshotListenError('peopleHubFirestore', `${TIME_OFF_COLLECTION}/teamVisible`, err);
      onError?.(err as Error);
    },
  );
}

function sortTimeOffByCreatedAtDesc(rows: TimeOffRequest[]): TimeOffRequest[] {
  return [...rows].sort((a, b) => {
    const am = a.createdAt?.toMillis?.() ?? 0;
    const bm = b.createdAt?.toMillis?.() ?? 0;
    return bm - am;
  });
}

export function subscribeOwnTimeOffRequests(
  userId: string,
  onData: (rows: TimeOffRequest[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  /** Single-field equality — no composite index. Sort client-side (newest first). */
  const q = query(collection(db, TIME_OFF_COLLECTION), where('userId', '==', userId));
  return onSnapshot(
    q,
    snap => {
      const rows: TimeOffRequest[] = [];
      snap.forEach(s => {
        rows.push({ id: s.id, ...(s.data() as Omit<TimeOffRequest, 'id'>) });
      });
      onData(sortTimeOffByCreatedAtDesc(rows));
    },
    err => {
      firestoreSnapshotListenError('peopleHubFirestore', `${TIME_OFF_COLLECTION}/own:${userId}`, err, {
        userId,
      });
      onError?.(err as Error);
    },
  );
}

export function subscribePendingTimeOffQueue(
  onData: (rows: TimeOffRequest[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  /** Single-field equality — no composite index. Sort client-side (newest first). */
  const q = query(collection(db, TIME_OFF_COLLECTION), where('status', '==', 'pending'));
  return onSnapshot(
    q,
    snap => {
      const rows: TimeOffRequest[] = [];
      snap.forEach(s => {
        rows.push({ id: s.id, ...(s.data() as Omit<TimeOffRequest, 'id'>) });
      });
      onData(sortTimeOffByCreatedAtDesc(rows));
    },
    err => {
      firestoreSnapshotListenError('peopleHubFirestore', `${TIME_OFF_COLLECTION}/pendingQueue`, err);
      onError?.(err as Error);
    },
  );
}

export async function createTimeOffRequest(input: {
  userId: string;
  displayName: string;
  start: Date;
  end: Date;
  type: TimeOffType;
  notes?: string;
}): Promise<void> {
  const showOnTeamCalendar = input.type !== 'sick';
  const now = Timestamp.now();
  await addDoc(collection(db, TIME_OFF_COLLECTION), {
    userId: input.userId,
    displayName: input.displayName,
    startDate: Timestamp.fromDate(startOfDay(input.start)),
    endDate: Timestamp.fromDate(startOfDay(input.end)),
    type: input.type,
    status: 'pending',
    showOnTeamCalendar,
    notes: input.notes?.trim() || '',
    createdAt: now,
    updatedAt: now,
  });
}

/** Admin / EA: add approved time off directly to the team calendar (any person, incl. contractors). */
export async function createAdminCalendarTimeOff(input: {
  userId: string;
  displayName: string;
  start: Date;
  end: Date;
  type: TimeOffType;
  notes?: string;
  createdByUserId: string;
  createdByName: string;
}): Promise<void> {
  const showOnTeamCalendar = input.type !== 'sick';
  const now = Timestamp.now();
  await addDoc(collection(db, TIME_OFF_COLLECTION), {
    userId: input.userId,
    displayName: input.displayName.trim(),
    startDate: Timestamp.fromDate(startOfDay(input.start)),
    endDate: Timestamp.fromDate(startOfDay(input.end)),
    type: input.type,
    status: 'approved',
    showOnTeamCalendar,
    calendarOnly: true,
    notes: input.notes?.trim() || '',
    createdByUserId: input.createdByUserId,
    createdByName: input.createdByName,
    reviewedByUserId: input.createdByUserId,
    reviewedByName: input.createdByName,
    reviewedAt: now,
    createdAt: now,
    updatedAt: now,
  });
}

export async function deleteAdminCalendarTimeOff(id: string): Promise<void> {
  await deleteDoc(doc(db, TIME_OFF_COLLECTION, id));
}

export async function updateTimeOffRequest(
  id: string,
  patch: Partial<
    Pick<
      TimeOffRequest,
      | 'startDate'
      | 'endDate'
      | 'type'
      | 'showOnTeamCalendar'
      | 'notes'
      | 'status'
      | 'reviewedByUserId'
      | 'reviewedByName'
      | 'reviewedAt'
      | 'rejectReason'
    >
  > & { updatedAt: Timestamp },
): Promise<void> {
  // Firestore v10 `updateDoc` UpdateData typing is narrower than Partial<Timestamp>; payload is valid at runtime.
  await updateDoc(doc(db, TIME_OFF_COLLECTION, id), patch as never);
}

export async function deletePendingTimeOffRequest(id: string): Promise<void> {
  await deleteDoc(doc(db, TIME_OFF_COLLECTION, id));
}

/** One-shot fetch of payroll snapshots for admin / EA grid. */
export async function fetchAllPayrollSnapshots(): Promise<
  (EmployeePayrollSnapshot & { id: string })[]
> {
  const snap = await getDocs(collection(db, PAYROLL_SNAPSHOTS_COLLECTION));
  const rows: (EmployeePayrollSnapshot & { id: string })[] = [];
  snap.forEach(s => {
    rows.push({ id: s.id, ...(s.data() as EmployeePayrollSnapshot) });
  });
  rows.sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
  return rows;
}

export async function fetchOwnPayrollSnapshot(
  uid: string,
): Promise<(EmployeePayrollSnapshot & { id: string }) | null> {
  const ref = doc(db, PAYROLL_SNAPSHOTS_COLLECTION, uid);
  const s = await getDoc(ref);
  if (!s.exists()) return null;
  return { id: s.id, ...(s.data() as EmployeePayrollSnapshot) };
}

function num(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return 0;
}

/**
 * Admins / executive assistants: create or update an employee leave profile.
 * Uses Firestore directly (same security rules as the Benefits UI) so it does not depend on
 * Cloud Functions runtime IAM. Scheduled accrual still runs in Cloud Functions.
 *
 * Pass `email` / `displayName` from CRM context; callers must not read `users/{uid}` (staff
 * cannot read other users’ profile docs under current rules).
 */
export async function saveEmployeeLeaveProfileAdmin(args: {
  targetUid: string;
  leaveBuckets: Record<LeaveBucketKey, { available: number; accrualPerPayRun: number }>;
  email: string | null;
  displayName: string | null;
}): Promise<void> {
  const actor = auth.currentUser?.uid;
  if (!actor) throw new Error('Sign in required');

  const snapRef = doc(db, PAYROLL_SNAPSHOTS_COLLECTION, args.targetUid);

  const now = Timestamp.now();
  await setDoc(
    snapRef,
    {
      userId: args.targetUid,
      email: args.email,
      displayName: args.displayName,
      balanceSource: 'internal',
      leaveBuckets: args.leaveBuckets,
      openingConfiguredAt: now,
      configuredByUserId: actor,
      updatedAt: now,
    },
    { merge: true },
  );
}

/** Admins / EAs: post “used hours” for a pay period (idempotent replace for that payDate). */
export async function applyLeaveUsedForPeriodAdmin(args: {
  targetUid: string;
  payDate: string;
  usedHours: Record<LeaveBucketKey, number>;
}): Promise<void> {
  const actor = auth.currentUser?.uid;
  if (!actor) throw new Error('Sign in required');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(args.payDate)) {
    throw new Error('payDate must be yyyy-MM-dd');
  }

  const snapRef = doc(db, PAYROLL_SNAPSHOTS_COLLECTION, args.targetUid);
  const adjRef = doc(db, PAYROLL_SNAPSHOTS_COLLECTION, args.targetUid, 'leavePeriodAdjustments', args.payDate);

  await runTransaction(db, async tx => {
    const snap = await tx.get(snapRef);
    if (!snap.exists()) throw new Error('No leave profile for user');

    const adjSnap = await tx.get(adjRef);
    const d = snap.data() || {};
    const lb = (d.leaveBuckets || {}) as Record<string, { available?: unknown; accrualPerPayRun?: unknown }>;

    const cur = emptyLeaveBuckets();
    for (const k of LEAVE_BUCKET_KEYS) {
      cur[k].available = num(lb[k]?.available);
      cur[k].accrualPerPayRun = num((lb[k] as { accrualPerPayRun?: unknown } | undefined)?.accrualPerPayRun);
    }

    let prevUsed: Record<LeaveBucketKey, number> | null = null;
    if (adjSnap.exists()) {
      const ad = adjSnap.data() || {};
      const pu = ad.usedHours as Record<string, unknown> | undefined;
      if (pu && typeof pu === 'object') {
        prevUsed = {
          sick: num(pu.sick),
          vacation: num(pu.vacation),
          paidTimeOff: num(pu.paidTimeOff),
          unpaidTimeOff: num(pu.unpaidTimeOff),
        };
      }
    }

    const next = emptyLeaveBuckets();
    for (const k of LEAVE_BUCKET_KEYS) {
      let av = cur[k].available;
      if (prevUsed) av += prevUsed[k];
      av -= args.usedHours[k];
      next[k] = { available: av, accrualPerPayRun: cur[k].accrualPerPayRun };
    }

    const t = Timestamp.now();
    tx.set(snapRef, { leaveBuckets: next, updatedAt: t }, { merge: true });
    tx.set(adjRef, {
      payDate: args.payDate,
      usedHours: args.usedHours,
      appliedByUserId: actor,
      appliedAt: t,
    });
  });
}
